import { CollectionRef, CollectionSummary } from "@/types/collection"
import { EntityRef, PlaceRef } from "@/types/entity"
import { FavoritePlace, Place, PlacePlaceholder } from "@/types/place"
import { SavedPlaceRefAndCollections, SavedSuggestions } from "@/types/saved"
import { CommissionSourceRef, TripRef } from "@/types/trip"
import { createSelector } from "@reduxjs/toolkit"

import { PagingMeta } from "@/lib/paging"
import { RootState } from "../redux-store"
import { baseApi } from "./base-api"
import { collectionsApi } from "./collections-api"

export const savedPlacesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSavedPlacesList: builder.query<SavedPlaceRefAndCollections[], void>({
      query: (_arg) => ({
        url: "/api/favorite_places/entity_refs",
      }),
      transformResponse: (response: { data: SavedPlaceRefAndCollections[] }) =>
        response.data,
      // Its own tag, so invalidating "SavedPlaces" for the full-place queries
      // below does not refetch every saved ref of a large library.
      providesTags: ["SavedPlaceRefs"],
      keepUnusedDataFor: Infinity,
    }),
    getSavedSuggestions: builder.query<
      SavedSuggestions,
      { q: string; limit?: number }
    >({
      query: ({ q, limit }) => {
        const params = new URLSearchParams({ q })
        if (limit) params.append("limit", limit.toString())
        return { url: `/api/favorite_places/suggest?${params}` }
      },
      providesTags: ["SavedPlaces", "Collections"],
    }),
    getSavedPlacesByTrip: builder.query<FavoritePlace[], TripRef>({
      query: (tripRef) => ({
        url: `/api/favorite_places/by_trip/${tripRef}`,
      }),
      transformResponse: (response: { data: FavoritePlace[] }) => response.data,
      providesTags: (result, error, tripRef) => [
        { type: "SavedPlaces", id: `trip-${tripRef}` },
        "SavedPlaces",
      ],
      keepUnusedDataFor: Infinity,
    }),
    getNearbySavedPlaces: builder.query<
      FavoritePlace[],
      { entityRef: PlaceRef; excludeTripRef?: TripRef }
    >({
      query: (args) => {
        const params = args.excludeTripRef
          ? `?ex_trip_ref=${args.excludeTripRef}`
          : ""
        return {
          url: `/api/favorite_places/nearby/${args.entityRef}${params}`,
        }
      },
      transformResponse: (response: { data: FavoritePlace[] }) => response.data,
      providesTags: ["SavedPlaces"],
      keepUnusedDataFor: Infinity,
    }),
    getSavedPlacesForAddingToTrip: builder.query<
      { favorite_places: FavoritePlace[]; meta: PagingMeta },
      { tripRef: TripRef; page?: number }
    >({
      query: (args) => {
        const params = new URLSearchParams({ per_page: "20" })
        if (args.page) params.append("page", args.page.toString())
        return {
          url: `/api/favorite_places/for_adding_to_trip/${args.tripRef}?${params}`,
        }
      },
      providesTags: (result, error, args) => [
        { type: "SavedPlaces", id: `for-trip-${args.tripRef}` },
        "SavedPlaces",
      ],
      keepUnusedDataFor: Infinity,
    }),
    addSavedPlace: builder.mutation<
      {
        favoritePlace: FavoritePlace
        savedCollectionSummary: CollectionSummary
      },
      {
        entity: Place
        tripRef?: TripRef
        collectionRef?: CollectionRef
        commissionSourceRef?: CommissionSourceRef
      }
    >({
      query: (args) => ({
        url: "/api/favorite_places",
        method: "POST",
        data: {
          entity_ref: args.entity.entity_ref,
          trip_ref: args.tripRef,
          collection_ref: args.collectionRef,
          commission_source_ref: args.commissionSourceRef,
        },
      }),
      transformResponse: (data: {
        favorite_place: FavoritePlace
        saved_collection_summary: CollectionSummary
      }) => ({
        favoritePlace: data.favorite_place,
        savedCollectionSummary: data.saved_collection_summary,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistic update for saved places list
        patches.push(
          dispatch(
            savedPlacesApi.util.updateQueryData(
              "getSavedPlacesList",
              undefined,
              (draft) => {
                const existingIndex = draft.findIndex(
                  (sp) => sp.entity_ref === args.entity.entity_ref
                )
                if (existingIndex !== -1) {
                  // Place already saved, add to collection
                  const existing = draft[existingIndex]
                  if (
                    args.collectionRef &&
                    !existing.collections?.includes(args.collectionRef)
                  ) {
                    existing.collections = [
                      ...(existing.collections || []),
                      args.collectionRef,
                    ]
                  }
                } else {
                  // New save
                  draft.unshift({
                    entity_ref: args.entity.entity_ref,
                    collections: args.collectionRef
                      ? [args.collectionRef]
                      : undefined,
                  })
                }
              }
            )
          )
        )

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
    }),
    removeSavedPlace: builder.mutation<
      { completely_removed: boolean },
      { entityRef: EntityRef; collectionRef?: CollectionRef }
    >({
      query: (args) => {
        const url = args.collectionRef
          ? `/api/favorite_places/${args.entityRef}?collection_ref=${args.collectionRef}`
          : `/api/favorite_places/${args.entityRef}`
        return { url, method: "DELETE" }
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistic update for saved places list
        patches.push(
          dispatch(
            savedPlacesApi.util.updateQueryData(
              "getSavedPlacesList",
              undefined,
              (draft) => {
                const index = draft.findIndex(
                  (sp) => sp.entity_ref === args.entityRef
                )
                if (index !== -1) {
                  if (args.collectionRef) {
                    // Remove from collection
                    const newCollections = draft[index].collections?.filter(
                      (c) => c !== args.collectionRef
                    )
                    // If no collections left, place is completely removed
                    if (!newCollections || newCollections.length === 0) {
                      draft.splice(index, 1)
                    } else {
                      draft[index].collections = newCollections
                    }
                  } else {
                    // Remove entirely
                    draft.splice(index, 1)
                  }
                }
              }
            )
          )
        )

        // Optimistic update for collection detail page (remove place from favorites)
        if (args.collectionRef) {
          try {
            patches.push(
              dispatch(
                collectionsApi.util.updateQueryData(
                  "getCollection",
                  { ref: args.collectionRef },
                  (draft) => {
                    // Remove the place from favorites array
                    draft.data.favorites = draft.data.favorites.filter(
                      (fav) => fav.entity.entity_ref !== args.entityRef
                    )
                    // Decrement favorites count
                    draft.data.favorites_count = Math.max(
                      0,
                      draft.data.favorites_count - 1
                    )
                  }
                )
              )
            )
          } catch {
            // Cache entry doesn't exist, ignore
          }

          dispatch(collectionsApi.util.invalidateTags(["Collections"]))
        }

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
    }),
    bulkAddSavedPlaces: builder.mutation<
      { savedCollectionSummary: CollectionSummary },
      {
        entityRefs: PlaceRef[]
        collectionRef: CollectionRef
        tripRef?: TripRef
      }
    >({
      query: (args) => ({
        url: "/api/favorite_places/bulk_create",
        method: "POST",
        data: {
          entity_refs: args.entityRefs,
          collection_ref: args.collectionRef,
          trip_ref: args.tripRef,
        },
      }),
      transformResponse: (response: {
        saved_collection_summary: CollectionSummary
      }) => ({
        savedCollectionSummary: response.saved_collection_summary,
      }),
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patches: Array<{ undo: () => void }> = []

        // Optimistic update for saved places list
        patches.push(
          dispatch(
            savedPlacesApi.util.updateQueryData(
              "getSavedPlacesList",
              undefined,
              (draft) => {
                for (const entityRef of args.entityRefs) {
                  const existingIndex = draft.findIndex(
                    (sp) => sp.entity_ref === entityRef
                  )
                  if (existingIndex !== -1) {
                    // Add collection to existing place
                    const existing = draft[existingIndex]
                    if (!existing.collections?.includes(args.collectionRef)) {
                      existing.collections = [
                        ...(existing.collections || []),
                        args.collectionRef,
                      ]
                    }
                  } else {
                    // Add new place
                    draft.unshift({
                      entity_ref: entityRef,
                      collections: [args.collectionRef],
                    })
                  }
                }
              }
            )
          )
        )

        try {
          await queryFulfilled
        } catch {
          patches.forEach((patch) => patch.undo())
        }
      },
    }),
  }),
})

export const {
  useGetSavedPlacesListQuery,
  useLazyGetSavedPlacesListQuery,
  useGetSavedSuggestionsQuery,
  useGetSavedPlacesByTripQuery,
  useLazyGetSavedPlacesByTripQuery,
  useGetNearbySavedPlacesQuery,
  useLazyGetNearbySavedPlacesQuery,
  useGetSavedPlacesForAddingToTripQuery,
  useLazyGetSavedPlacesForAddingToTripQuery,
  useAddSavedPlaceMutation,
  useRemoveSavedPlaceMutation,
  useBulkAddSavedPlacesMutation,
  usePrefetch,
} = savedPlacesApi

const selectSavedPlacesResult =
  savedPlacesApi.endpoints.getSavedPlacesList.select()

export const selectSavedPlacesList = createSelector(
  selectSavedPlacesResult,
  (result) => result.data ?? []
)

export const selectSavedPlacesCount = createSelector(
  [selectSavedPlacesResult],
  (queryResult) => queryResult.data?.length ?? 0
)

export const selectSavedPlacesIsLoading = createSelector(
  selectSavedPlacesResult,
  (result) => result.isLoading
)

export const selectSavedPlacesListFetched = createSelector(
  selectSavedPlacesResult,
  (result) => result.isSuccess
)

export const selectSavedPlaceRefs = createSelector(
  [selectSavedPlacesList],
  (savedPlacesList) => {
    return new Set(savedPlacesList.map((place) => place.entity_ref))
  }
)

export const selectIsSavedPlace = createSelector(
  [
    selectSavedPlaceRefs,
    (_state: RootState, place: Place | PlacePlaceholder) => place,
  ],
  (savedPlaceRefs, place) => {
    return savedPlaceRefs.has(place.entity_ref)
  }
)

const selectSavedPlacesByRef = createSelector(
  [selectSavedPlacesList],
  (savedPlacesList) =>
    new Map(
      savedPlacesList.map((savedPlace) => [savedPlace.entity_ref, savedPlace])
    )
)

export const selectIsPlaceInCollection = createSelector(
  [
    selectSavedPlacesByRef,
    (_state: RootState, placeRef: PlaceRef) => placeRef,
    (_state: RootState, _placeRef: PlaceRef, collectionRef: CollectionRef) =>
      collectionRef,
  ],
  (savedPlacesByRef, placeRef, collectionRef) =>
    savedPlacesByRef.get(placeRef)?.collections?.includes(collectionRef) ||
    false
)

export const selectIsPlaceInAnyCollection = createSelector(
  [
    selectSavedPlacesByRef,
    (_state: RootState, place: Place | PlacePlaceholder) => place,
  ],
  (savedPlacesByRef, place) => {
    const collections = savedPlacesByRef.get(place.entity_ref)?.collections
    return collections && collections.length > 0
  }
)

export const selectPlaceCollectionsCount = createSelector(
  [
    selectSavedPlacesByRef,
    (_state: RootState, place: Place | PlacePlaceholder) => place,
  ],
  (savedPlacesByRef, place) =>
    savedPlacesByRef.get(place.entity_ref)?.collections?.length || 0
)
